/**
 * AffectsScoreBadge — small warning-toned chip rendered next to any
 * preference field that lives in the `scoreAffecting` band
 * (Configuration Upgrade CU4).
 *
 * Today only `kpiWeightOverrides` (feasibility) mutates the
 * deterministic score — every other field is narrative-only. Without
 * the chip, users wonder why their cooling-preference toggle doesn't
 * move the number. CU4 makes the score-vs-narrative split explicit.
 *
 * Hover tooltip: "Changing this field will move the deterministic
 * score; other fields only frame the narrative."
 *
 * The shared FieldRow primitive renders this automatically when its
 * `fieldKey` matches the validation context's scoreAffectingKeys.
 * Blocks that don't sit in a FieldRow (KPI weights inside a Tabs
 * panel) render the badge directly via this primitive.
 */
import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

export interface AffectsScoreBadgeProps {
  /** Field key — used for test reachability (`affects-score-badge-{key}`). */
  fieldKey: string;
}

const TOOLTIP_COPY =
  'Changing this field will move the deterministic score; other fields only frame the narrative.';

export const AffectsScoreBadge: React.FC<AffectsScoreBadgeProps> = ({ fieldKey }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex items-center type-meta uppercase tracking-widest text-warning rounded-md border border-warning/40 bg-warning/5 px-1 py-0.5 cursor-help"
          data-testid={`affects-score-badge-${fieldKey}`}
        >
          Affects score
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-xs">{TOOLTIP_COPY}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
